import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { CheckCircle, MapPin, AlertTriangle, Phone, Mail, Info } from 'lucide-react'
import TopHeader from '../components/layout/TopHeader.jsx'
import { useCartStore, useAuthStore } from '../store/index.js'
import { cartService, addressService, authService } from '../services/api.js'
import { useQuery } from '@tanstack/react-query'
import toast from 'react-hot-toast'

// Two steps only — no payment (invoicing handled separately)
const STEPS = ['Delivery', 'Review']

function fmtAddress(addr) {
  if (!addr) return ''
  return [addr.address_line1, addr.address_line2, addr.city, addr.postal_code]
    .filter(Boolean).join(', ')
}

function addrLabel(addr) {
  return addr.address_type || 'Delivery Address'
}

export default function Checkout() {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuthStore()
  const { items, clearCart } = useCartStore()

  const [step, setStep]                       = useState(0)
  const [loading, setLoading]                 = useState(false)
  const [addresses, setAddresses]             = useState([])
  const [selectedAddress, setSelectedAddress] = useState(null)
  const [deliveryNotes, setDeliveryNotes]     = useState('')

  const loadData = useCallback(async () => {
    if (!isAuthenticated) return
    try {
      const [, addrData] = await Promise.all([
        cartService.get(),
        addressService.getAll(),
      ])
      const addrs = Array.isArray(addrData) ? addrData : (addrData?.addresses || [])
      setAddresses(addrs)
      if (addrs.length > 0) setSelectedAddress(addrs[0])
    } catch (err) {
      console.error(err)
    }
  }, [isAuthenticated])

  useEffect(() => { loadData() }, [loadData])

  // Compute from items directly — getter is lost after JSON hydration
  const cartTotal  = items.reduce((sum, i) => sum + (parseFloat(i.price) || 0) * (i.qty || i.quantity || 1), 0)
  const grandTotal = cartTotal   // no delivery fee — B2B distributor

  const { data: customerProfile } = useQuery({
    queryKey: ['customer', 'me'],
    queryFn: authService.customerMe,
    enabled: isAuthenticated,
    staleTime: 60 * 1000,
    retry: false,
  })

  const handlePlaceOrder = async () => {
    setLoading(true)
    try {
      const cartItemsToSend = items.map(i => ({
        product_id: i.id,
        quantity:   i.qty || i.quantity || 1,
      }))

      if (!cartItemsToSend.length) { toast.error('Your cart is empty'); return }

      const res = await cartService.checkout({
        items:            cartItemsToSend,
        delivery_address: selectedAddress ? fmtAddress(selectedAddress) : '',
        delivery_notes:   deliveryNotes,
      })

      const orderId = res?.order_number || res?.id || ''
      clearCart()
      navigate('/order-success', { state: { orderId } })
    } catch (err) {
      toast.error(err.message || 'Failed to place order')
    } finally {
      setLoading(false)
    }
  }

  // ── Guards ─────────────────────────────────────────────────────────────────
  if (items.length === 0) return (
    <div className="min-h-screen bg-surface-dark flex flex-col items-center justify-center px-6 text-center">
      <span className="text-6xl mb-4">🛒</span>
      <h2 className="font-heading text-white text-2xl mb-3">Your cart is empty</h2>
      <button onClick={() => navigate('/shop')} className="btn-primary">Browse Products</button>
    </div>
  )

  if (customerProfile?.can_order === false) return (
    <div className="min-h-screen bg-surface-dark flex flex-col items-center justify-center px-6 text-center">
      <div className="w-20 h-20 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mx-auto mb-6">
        <AlertTriangle size={36} className="text-amber-400" />
      </div>
      <h2 className="font-heading text-white text-2xl mb-3">Account On Hold</h2>
      <p className="text-gray-400 text-sm mb-6 max-w-xs">
        Your account has an outstanding balance. Please contact us to re-enable ordering.
      </p>
      <div className="space-y-3 w-full max-w-xs">
        <a href="tel:+27123231520" className="flex items-center justify-center gap-3 w-full py-3 rounded-xl bg-surface-card border border-surface-border text-primary text-sm font-display">
          <Phone size={16} /> 012 323 1520
        </a>
        <a href="mailto:orders@tonysliquor.co.za" className="flex items-center justify-center gap-3 w-full py-3 rounded-xl bg-surface-card border border-surface-border text-primary text-sm font-display">
          <Mail size={16} /> orders@tonysliquor.co.za
        </a>
        <button onClick={() => navigate('/shop')} className="w-full py-3 rounded-xl bg-surface-card border border-surface-border text-gray-400 text-sm font-display">
          Continue Browsing
        </button>
      </div>
    </div>
  )

  return (
    <div className="min-h-screen bg-surface-dark pb-28">
      <TopHeader title="Checkout" showBack backTo="/cart" showCart={false} />

      {/* Step indicator */}
      <div className="flex items-center justify-center gap-2 pt-4 pb-2 px-4">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center gap-2">
            <div className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold font-display transition-colors ${
              i < step ? 'bg-green-600 text-white' :
              i === step ? 'bg-primary text-white' : 'bg-surface-elevated text-gray-500'
            }`}>
              {i < step ? <CheckCircle size={14} /> : i + 1}
            </div>
            <span className={`text-xs font-display ${i === step ? 'text-white' : 'text-gray-500'}`}>{s}</span>
            {i < STEPS.length - 1 && (
              <div className={`h-px w-8 ${i < step ? 'bg-green-600' : 'bg-surface-border'}`} />
            )}
          </div>
        ))}
      </div>

      <div className="px-4 pt-4 space-y-4 pb-4">

        {/* ── Step 0: Delivery ─────────────────────────────────────────────── */}
        {step === 0 && (
          <>
            <h3 className="font-heading text-white text-lg">Delivery Address</h3>

            {/* Policy notice */}
            <div className="bg-primary/5 border border-primary/20 rounded-2xl p-3 flex items-start gap-3">
              <Info size={15} className="text-primary mt-0.5 flex-shrink-0" />
              <p className="text-gray-400 text-xs font-display leading-relaxed">
                As a licensed bulk distributor, we can only deliver to your registered address on file.
                To update, call <a href="tel:0123231520" className="text-primary font-medium">012 323 1520</a>.
              </p>
            </div>

            {addresses.length === 0 ? (
              <div className="bg-surface-card rounded-2xl border border-surface-border p-6 text-center">
                <MapPin size={32} className="text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400 font-display text-sm mb-1">No delivery address on file</p>
                <p className="text-gray-500 font-display text-xs">
                  Contact us on <a href="tel:0123231520" className="text-primary">012 323 1520</a> to register your address
                </p>
              </div>
            ) : (
              addresses.map(addr => (
                <button
                  key={addr.id}
                  onClick={() => setSelectedAddress(addr)}
                  className={`w-full text-left bg-surface-card rounded-2xl border p-4 transition-colors ${
                    selectedAddress?.id === addr.id ? 'border-primary bg-primary/5' : 'border-surface-border'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-xl flex items-center justify-center mt-0.5 flex-shrink-0 ${
                      selectedAddress?.id === addr.id ? 'bg-primary' : 'bg-surface-elevated'
                    }`}>
                      <MapPin size={14} className={selectedAddress?.id === addr.id ? 'text-white' : 'text-gray-400'} />
                    </div>
                    <div className="flex-1">
                      <span className="font-display font-semibold text-white text-sm">{addrLabel(addr)}</span>
                      <p className="font-display text-gray-400 text-sm mt-0.5">{addr.address_line1}</p>
                      {addr.address_line2 && <p className="font-display text-gray-400 text-sm">{addr.address_line2}</p>}
                      <p className="font-display text-gray-400 text-sm">{addr.city}{addr.postal_code ? `, ${addr.postal_code}` : ''}</p>
                    </div>
                    {selectedAddress?.id === addr.id && (
                      <CheckCircle size={16} className="text-primary flex-shrink-0 mt-0.5" />
                    )}
                  </div>
                </button>
              ))
            )}

            <div>
              <label className="block text-gray-400 text-xs font-display mb-1.5">Delivery Notes (optional)</label>
              <textarea
                value={deliveryNotes}
                onChange={e => setDeliveryNotes(e.target.value)}
                placeholder="e.g. Gate code, delivery instructions..."
                rows={2}
                className="w-full bg-surface-card border border-surface-border rounded-xl px-3 py-2.5 text-white text-sm font-display placeholder-gray-600 resize-none focus:outline-none focus:border-primary"
              />
            </div>
          </>
        )}

        {/* ── Step 1: Review ───────────────────────────────────────────────── */}
        {step === 1 && (
          <>
            <h3 className="font-heading text-white text-lg">Review Order</h3>

            {/* Delivery address */}
            {selectedAddress && (
              <div className="bg-surface-card rounded-2xl border border-surface-border p-4">
                <div className="flex items-center gap-2 mb-2">
                  <MapPin size={14} className="text-primary" />
                  <span className="font-display font-semibold text-white text-sm">Delivering to</span>
                </div>
                <p className="text-gray-300 text-sm font-display">{fmtAddress(selectedAddress)}</p>
                {deliveryNotes && (
                  <p className="text-gray-500 text-xs font-display mt-1.5">📝 {deliveryNotes}</p>
                )}
              </div>
            )}

            {/* Items */}
            <div className="bg-surface-card rounded-2xl border border-surface-border p-4">
              <h4 className="font-display font-semibold text-white mb-3 text-sm">
                Items ({items.length})
              </h4>
              <div className="space-y-2">
                {items.map((item, idx) => {
                  const price = parseFloat(item.price || 0)
                  const qty   = item.qty || item.quantity || 1
                  return (
                    <div key={idx} className="flex justify-between items-start gap-3">
                      <span className="text-gray-300 text-sm font-display leading-tight flex-1 line-clamp-2">
                        {item.name || 'Product'}
                        <span className="text-gray-500 ml-1">×{qty}</span>
                      </span>
                      <span className="text-white font-display font-semibold text-sm whitespace-nowrap">
                        R{(price * qty).toFixed(2)}
                      </span>
                    </div>
                  )
                })}
              </div>
              <div className="border-t border-surface-border mt-3 pt-3">
                <div className="flex justify-between">
                  <span className="font-display font-bold text-white">Total</span>
                  <span className="font-heading text-gold text-lg">R{grandTotal.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Info notice */}
            <div className="bg-surface-card rounded-2xl border border-surface-border p-4 flex items-start gap-3">
              <Info size={15} className="text-primary mt-0.5 flex-shrink-0" />
              <p className="text-gray-400 text-sm font-display leading-relaxed">
                A confirmation email will be sent when your order is placed. No payment is taken online — invoicing is handled separately.
              </p>
            </div>

            {/* Legal */}
            <div className="bg-yellow-900/20 border border-yellow-700/30 rounded-2xl p-3">
              <p className="text-yellow-300 text-xs font-display text-center">
                ⚠️ By placing this order you confirm you are 18+ years old and comply with the Liquor Act.
              </p>
            </div>
          </>
        )}
      </div>

      {/* Fixed CTA */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-surface-card border-t border-surface-border px-4 pt-3"
        style={{ paddingBottom: 'max(0.75rem, var(--safe-bottom))' }}>
        <div className="flex gap-3">
          {step > 0 && (
            <button onClick={() => setStep(s => s - 1)} className="btn-outline px-6">Back</button>
          )}
          {step < STEPS.length - 1 ? (
            <button
              onClick={() => {
                if (step === 0 && addresses.length > 0 && !selectedAddress) {
                  toast.error('Please select a delivery address')
                  return
                }
                setStep(s => s + 1)
              }}
              className="btn-primary flex-1"
            >
              Review Order →
            </button>
          ) : (
            <button
              onClick={handlePlaceOrder}
              disabled={loading || (addresses.length > 0 && !selectedAddress)}
              className="btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading
                ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                : `Place Order · R${grandTotal.toFixed(2)}`
              }
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
